import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, InputNumber, Row, App, Select, Upload, Space, Typography, Table } from 'antd';
import { DeleteOutlined, PictureOutlined, UploadOutlined } from '@ant-design/icons';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import RichTextEditor from '../../components/RichTextEditor';
import {
  getCompetitionById,
  createCompetition,
  updateCompetition,
  uploadCompetitionMedia,
} from '../../services/competitions';
import {
  listCompetitionStatuses,
  listPeriods,
  listGenders,
  listModalities,
  listCompetitionCriteria,
  listCompetitionJudges,
  listCompetitionRegions,
} from '../../services/catalogs';
import { resolveMedia } from '../../utils/media';
import { DatePicker } from 'antd';
import { TimePicker } from 'antd';
import { Switch } from 'antd';
import { Checkbox } from 'antd';
import { Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { Alert } from 'antd';
import { Divider } from 'antd';
import usePermissions from '../../hooks/usePermissions';


const { useApp } = App;
const { Dragger } = Upload;
const { Text } = Typography;

const CompetitionUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();
  const { canAny } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(undefined);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [originalPreviewPath, setOriginalPreviewPath] = useState(null);
  const [competitionData, setCompetitionData] = useState(null);
  const [instructionsState, setInstructionsState] = useState(() => EditorState.createEmpty());
  const [regions, setRegions] = useState([]);
  const [regionIds, setRegionIds] = useState([]);
  const [judges, setJudges] = useState([]);
  const [judgeIds, setJudgesIds] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [criteriaIds, setCriteriaIds] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const availableJudges = useMemo(() => {
    if (!regionIds.length) return [];
      return judges.filter(judge => {
        const isAlreadySelected = judgeIds.some(
          j => j.id === Number(judge.id),
        );
        if (isAlreadySelected) return false;
        const belongsToSelectedRegion = judge.userRegions?.some(
          ur => regionIds.includes(ur.region_id),
        );
        return belongsToSelectedRegion;
      });
  }, [judges, judgeIds, regionIds]);

  const availableCriteria = criteria.filter(
    criteria => !criteriaIds.some(rs => rs.id === Number(criteria.id))
  );

  // Catálogos
  const [periods, setPeriods] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [genders, setGenders] = useState([]);
  const [modalities, setModalities] = useState([]);

  const [files, setFiles] = useState({
    file1: null,
    file2: null,
    file3: null,
  });

  const toISODate = (date) =>
    date ? new Date(date).toISOString() : undefined;

  const buildStage = ({
    type,
    order,
    startDate,
    startTime,
    endDate,
    endTime,
  }) => ({
    type,
    order,
    startDate: startDate ? dayjs(String(startDate).slice(0, 10), 'YYYY-MM-DD') : undefined,
    endDate: endDate ? dayjs(String(endDate).slice(0, 10), 'YYYY-MM-DD') : undefined,
    startTime: startTime ? dayjs(startTime, 'HH:mm') : undefined,
    endTime: endTime ? dayjs(endTime, 'HH:mm') : undefined,
  });


  const regionColumns = [
    {
      title: 'Incluir',
      dataIndex: 'checkbox',
      render: (text, region) => (
        <Checkbox
          value={region.id}
          checked={regionIds.includes(region.id)}
          onChange={() => {
            const isSelected = regionIds.includes(region.id);

            let newRegionIds;
            let newJudgeIds = [...judgeIds];

            if (isSelected) {
              newRegionIds = regionIds.filter(id => id !== region.id);

              newJudgeIds = judgeIds.filter(judge =>
                judge.userRegions?.some(
                  ur => ur.region_id !== region.id
                )
              );
            } else {
              newRegionIds = [...regionIds, region.id];

              const judgesFromRegion = judges.filter(judge =>
                judge.userRegions?.some(
                  ur => ur.region_id === region.id
                )
              );

              const mergedJudges = [
                ...newJudgeIds,
                ...judgesFromRegion.filter(
                  j => !newJudgeIds.some(existing => existing.id === j.id)
                ),
              ];

              newJudgeIds = mergedJudges;
            }

            setRegionIds(newRegionIds);
            setJudgesIds(newJudgeIds);
          }}
        />
      ),
    },
    {
      title: 'Región',
      dataIndex: 'description',
      render: (text) => text,
    },
  ];

  const criteriaColumns = [
    {
      title: 'Criterio',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (value, record) => (
        <InputNumber
          min={1}
          value={value}
          onChange={(newValue) => handleScoreChange(record.id, newValue)}
        />
      ),
    },
    {
      title: 'Orden desempate',
      dataIndex: 'tie_breaking_order',
      key: 'tie_breaking_order',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="¿Eliminar criterio?"
          onConfirm={() => removeCriteria(record.id)}
        >
          <DeleteOutlined
            style={{ color: '#ff4d4f', cursor: 'pointer' }}
          />
        </Popconfirm>
      ),
    },
  ];

  const judgeColumns = [
    {
      title: 'Nombre',
      key: 'name',
      render: (_, record) =>
        `${record.firstName} ${record.lastName} ${record.secondLastName || ''}`,
    },
    {
      title: 'Regiones',
      dataIndex: 'regions',
      key: 'regions',
      render: (_, record) =>
        record.userRegions
        ?.map(ur => ur.region?.description)
        .filter(Boolean)
        .join(', ') || '-',
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title="¿Eliminar juez?"
          onConfirm={() => removeJudge(record.id)}
        >
          <DeleteOutlined
            style={{ color: '#ff4d4f', cursor: 'pointer' }}
          />
        </Popconfirm>
      ),
    },
  ];

  const stages = Form.useWatch('stages', form);

  const callStage = stages?.CALL;
  const resultsStage = stages?.RESULTS_PUBLICATION;

  const startCompetition =
    callStage?.startDate && callStage?.startTime
      ? dayjs(callStage.startDate)
          .hour(dayjs(callStage.startTime).hour())
          .minute(dayjs(callStage.startTime).minute())
          .format('DD/MM/YYYY hh:mm A')
      : '-';

  const endCompetition =
    resultsStage?.startDate && resultsStage?.startTime
      ? dayjs(resultsStage.startDate)
          .hour(dayjs(resultsStage.startTime).hour())
          .minute(dayjs(resultsStage.startTime).minute())
          .format('DD/MM/YYYY hh:mm A')
      : '-';


  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [
          statusesData,
          periodsData,
          gendersData,
          modalitiesData,
          criteriaData,
          regionsData,
          judgesData,
        ] = await Promise.all([
          listCompetitionStatuses(),
          listPeriods(),
          listGenders(),
          listModalities(),
          listCompetitionCriteria(),
          listCompetitionRegions(),
          listCompetitionJudges(),
        ]);
        setPeriods(periodsData || []);
        setStatuses(statusesData || []);
        setGenders(gendersData || []);
        setModalities(modalitiesData || []);
        setCriteria(criteriaData || []);
        setRegions(regionsData || []);
        setJudges(judgesData || []);
        if(!isEdit){
          const defaultCriteria = criteriaData.filter(criterion => criterion.include_default === true);
          const allRegionIds = regionsData.map(region => region.id);
          setRegionIds(allRegionIds);
          setCriteriaIds(defaultCriteria);
          const linkedJudges = judgesData.filter(judge =>
            judge.userRegions?.some(ur =>
              allRegionIds.includes(ur.region_id)
            )
          );

          setJudgesIds(linkedJudges);
        }
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
        message.error('No se pudieron cargar los catálogos');
      }
    };
    loadCatalogs();
  }, [message, isEdit]);

  useEffect(() => {
    form.setFieldsValue({
      statusId: 1,
      publicVote: 30,
      juryVote: 70
    });
    if (!isEdit || !id) return;
    setLoading(true);
    getCompetitionById(id)
      .then((competition) => {
        if(competition?.data === null){
          setCompetitionData(null);
          return;
        }
        setCompetitionData(competition);
        const initialRegionIds = competition.regions.map(r => r.regionId);
        setRegionIds(initialRegionIds);

        const stages = competition.stages?.length
          ? competition.stages.reduce((acc, stage) => {
              acc[stage.type] = buildStage({
                type: stage.type,
                order: stage.order,
                startDate: stage.startDate,
                endDate: stage.endDate,
                startTime: stage.startTime,
                endTime: stage.endTime,
              });
              return acc;
            }, {})
          : {
              CALL: buildStage({
                type: 'CALL',
                order: 1,
                startDate: competition.startDate,
                startTime: competition.startHour,
                endDate: competition.startDate,
                endTime: competition.startHour,
              }),
              EVALUATION: buildStage({
                type: 'EVALUATION',
                order: 2,
              }),
              VOTING: buildStage({
                type: 'VOTING',
                order: 3,
              }),
              RESULTS_PUBLICATION: buildStage({
                type: 'RESULTS_PUBLICATION',
                order: 4,
                startDate: competition.endDate,
                startTime: competition.endHour,
                endDate: competition.endDate,
                endTime: competition.endHour,
              }),
            };


        form.setFieldsValue({
          description: competition.description || undefined,
          modalityId: competition.modalityId,
          periodId: competition.periodId,
          genderId: competition.genderId,
          statusId: competition.statusId,
          sendNotification: Boolean(competition.sendNotification),
          folio: competition.folio ?? undefined,
          daysBefore: competition.daysBefore || undefined,
          finalistLimit: competition.finalistLimit || undefined,
          publicVote: competition.publicVote || undefined,
          juryVote: competition.juryVote || undefined,
          stages: stages,
        });
        setFiles({
          file1: competition.urlCompetitionRules
            ? {
                uid: "-1",
                name: "CompetitionRules.pdf",
                status: "done",
                url: resolveMedia(competition.urlCompetitionRules),
                type: "application/pdf",
                originFileObj: null,
              }
            : null,
          file2: competition.urlGeneralRules
            ? {
                uid: "-2",
                name: "GeneralRules.pdf",
                status: "done",
                url: resolveMedia(competition.urlGeneralRules),
                type: "application/pdf",
                originFileObj: null,
              }
            : null,
          file3: competition.urlTermsConditions
            ? {
                uid: "-3",
                name: "TermsCons.pdf",
                status: "done",
                url: resolveMedia(competition.urlTermsConditions),
                type: "application/pdf",
                originFileObj: null,
              }
            : null,
        });

        if (competition.instructions) {
          const contentBlock = htmlToDraft(competition.instructions);
          if (contentBlock) {
            const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
            const newEditorState = EditorState.createWithContent(contentState);
            setInstructionsState(newEditorState);
          }
        }

        if (competition.s3KeyImage) {
          setOriginalPreviewPath(competition.s3KeyImage);
          setImagePreview(resolveMedia(competition.s3KeyImage));
        }
      })
      .catch((e) => {
        console.error('Error al cargar la competencia:', e);
        message.error('No se pudo cargar la competencia');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  useEffect(() => {
    if (!competitionData) return;
    const initialCriteria = competitionData.criteria
      .map(c => {
        const criterion = criteria.find(ac => ac.id === c.criteriaId);
        return criterion ? { ...criterion, score: c.score } : null;
      })
      .filter(Boolean);
    const initialJudges = competitionData.judges
      .map(j => judges.find(u => u.id === j.userId))
      .filter(Boolean);
    setCriteriaIds(initialCriteria);
    setJudgesIds(initialJudges);
  }, [competitionData, criteria, judges]);

  useEffect(() => {
    setJudgesIds(prev =>
      prev.filter(judge =>
        judges.find(j =>
          j.id === judge.id &&
          j.userRegions.some(ur =>
            regionIds.includes(ur.region_id),
          ),
        ),
      ),
    );
  }, [regionIds, judges]);



  const generateFolio = ({ modalityId, periodId, genderId, startDate }) => {
    const modality = modalities.find(m => m.id === modalityId);
    const modalityKey = modality ? modality.code.toUpperCase() : '';

    const period = periods.find(p => p.id === periodId);
    const periodKey = period ? period.description[0].toUpperCase() : '';

    const gender = genders.find(g => g.id === genderId);
    const genderKey = gender ? gender.description[0].toUpperCase() : '';
    const dateKey = startDate.format('YYMMDD');

    return `${modalityKey}${periodKey}${genderKey}-${dateKey}`;
  };

  const handleFormChange = (changedValues) => {
    const { modalityId, periodId, genderId, stages } = form.getFieldsValue();
    const startDate = stages?.CALL?.startDate;
    if (modalityId && periodId && genderId && startDate) {
      const folio = generateFolio({ modalityId, periodId, genderId, startDate:dayjs(startDate) });
      form.setFieldsValue({
        folio: folio
      });
    } else {
      form.setFieldsValue({
        folio: ''
      });
    }
  };

  const validateStages = (stages) => {
    const order = ['CALL', 'EVALUATION', 'VOTING', 'RESULTS_PUBLICATION'];
    const STAGE_LABELS = {
      CALL: 'Convocatoria',
      EVALUATION: 'Evaluación',
      VOTING: 'Votación',
      RESULTS_PUBLICATION: 'Publicación de resultados',
    };


    let lastEnd = null;

    for (const type of order) {
      const stage = stages[type];
      if (!stage) continue;

      const start = dayjs(stage.startDate)
        .hour(dayjs(stage.startTime).hour())
        .minute(dayjs(stage.startTime).minute());

      const end = dayjs(stage.endDate || stage.startDate)
        .hour(dayjs(stage.endTime || stage.startTime).hour())
        .minute(dayjs(stage.endTime || stage.startTime).minute());

      // inicio > fin
      if (end.isBefore(start)) {
        return `La fecha fin de ${STAGE_LABELS[type] || type} no puede ser anterior a su inicio`;
      }

      // solapamiento
      if (lastEnd && start.isBefore(lastEnd)) {
        return `${STAGE_LABELS[type] || type} debe iniciar después de finalizar la etapa anterior`;
      }

      lastEnd = end;
    }

    return null;
  };


  const uploadProps = useMemo(
    () => ({
      name: 'file',
      multiple: false,
      maxCount: 1,
      beforeUpload: (file) => {
        const isImage = file.type.startsWith('image/');
        const isValidFormat = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';

        if (!isImage || !isValidFormat) {
          message.error('Solo se permiten archivos .jpg o .png');
          return Upload.LIST_IGNORE;
        }

        const maxSize = 5;
        const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
        if (!isLtMaxSize) {
          message.error(`El archivo debe pesar menos de ${maxSize}MB`);
          return Upload.LIST_IGNORE;
        }

        setImageFile(file);
        setImageRemoved(false);
        setImagePreview(URL.createObjectURL(file));

        return false;
      },
      onRemove: () => {
        setImageFile(null);
        setImagePreview(undefined);
        setImageRemoved(true);
      },
      showUploadList: false,
    }),
    [message]
  );

  const onInstructionsChange = (newEditorState) => {
    setInstructionsState(newEditorState);
  };

  const onSubmit = async (values) => {
    try {
      setLoading(true);

      const stageError = validateStages(values.stages);

      if (stageError) {
        message.error(stageError);
        setLoading(false);
        return;
      }

      let previewPathToSend = undefined;

      if (imageFile) {
        const uploadResult = await uploadCompetitionMedia(imageFile);
        previewPathToSend = uploadResult.url || uploadResult.preview_url;
      } else if (imageRemoved) {
        if (!isEdit) {
          message.error('La imagen es requerida');
          setLoading(false);
          return;
        }
        previewPathToSend = null;
      } else if (isEdit && originalPreviewPath) {
        previewPathToSend = originalPreviewPath;
      } else if (!isEdit) {
        message.error('La imagen es requerida');
        setLoading(false);
        return;
      }

      const uploadResults = {};
      for (const key of Object.keys(files)) {
        const file = files[key];
        if (file && file.file) {
          try {
            const result = await uploadCompetitionMedia(file.file);
            uploadResults[key] = result.url || result.preview_url;
          } catch (error) {
            console.error(`Error subiendo ${key}:`, error);
          }
        } 
        else if(file && file.url){
          uploadResults[key] = file.url || file.preview_url;
        } else {
          console.log(`Archivo ${key} no válido`);
        }
      }
      const instructionsHtml = instructionsState.getCurrentContent().hasText()
        ? draftToHtml(convertToRaw(instructionsState.getCurrentContent()))
        : undefined;
      const payload = {
        description: values.description?.trim() || undefined,
        ...(instructionsHtml && { instructions: instructionsHtml }),
        ...(previewPathToSend !== undefined && { s3KeyImage: previewPathToSend }),
        modalityId: values.modalityId,
        periodId: values.periodId,
        genderId: values.genderId,
        statusId: values.statusId,
        sendNotification: values.sendNotification,
        folio: values.folio ?? undefined,
        daysBefore: values.daysBefore ?? undefined,
        regionIds: regionIds,
        judgeIds: judgeIds,
        criteriaIds: criteriaIds,
        s3KeyCompetitionRules: uploadResults.file1 ?? undefined,
        urlCompetitionRules: uploadResults.file1 ?? undefined,
        s3KeyGeneralRules: uploadResults.file2 ?? undefined,
        urlGeneralRules: uploadResults.file2 ?? undefined,
        s3KeyTermsConditions: uploadResults.file3 ?? undefined,
        urlTermsConditions: uploadResults.file3 ?? undefined,
        finalistLimit: values.finalistLimit ?? undefined,
        publicVote: values.publicVote || undefined,
        juryVote: values.juryVote || undefined,
        stages: [
          {
            type: 'CALL',
            order: 1,
            startDate: toISODate(values.stages.CALL.startDate),
            endDate: toISODate(values.stages.CALL.endDate),
            startTime: dayjs(values.stages.CALL.startTime).format('HH:mm'),
            endTime: dayjs(values.stages.CALL.endTime).format('HH:mm'),
          },
          {
            type: 'EVALUATION',
            order: 2,
            startDate: toISODate(values.stages.EVALUATION.startDate),
            endDate: toISODate(values.stages.EVALUATION.endDate),
            startTime: dayjs(values.stages.EVALUATION.startTime).format('HH:mm'),
            endTime: dayjs(values.stages.EVALUATION.endTime).format('HH:mm'),
          },
          {
            type: 'VOTING',
            order: 3,
            startDate: toISODate(values.stages.VOTING.startDate),
            endDate: toISODate(values.stages.VOTING.endDate),
            startTime: dayjs(values.stages.VOTING.startTime).format('HH:mm'),
            endTime: dayjs(values.stages.VOTING.endTime).format('HH:mm'),
          },
          {
            type: 'RESULTS_PUBLICATION',
            order: 4,
            startDate: toISODate(values.stages.RESULTS_PUBLICATION.startDate),
            endDate: toISODate(values.stages.RESULTS_PUBLICATION.startDate),
            startTime: dayjs(values.stages.RESULTS_PUBLICATION.startTime).format('HH:mm'),
            endTime: dayjs(values.stages.RESULTS_PUBLICATION.startTime).format('HH:mm'),
          },
        ],
      };

      if (isEdit && id) {
        await updateCompetition(id, payload);
        message.success('competencia actualizada correctamente');
      } else {
        await createCompetition(payload);
        message.success('competencia creada correctamente');
      }

      navigate('/competitions/all');
    } catch (err) {
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al guardar la competencia';

      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];

        if (issues.length > 0) {
          form.setFields([]);

          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message)
            .map((issue) => {
              let fieldPath = issue.path;

              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }

              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });

          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
          }

          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = (file, fileKey) => {
      if (file.status === 'removed') {
        return;
      }
      if (file.type !== "application/pdf") {
        message.error("Solo se permiten archivos PDF");
        return false;
      }
      const url = URL.createObjectURL(file);
      setFiles(prev => ({
        ...prev,
        [fileKey]: {
          file,
          name: file.name,
          url,
        },
      }));

      return false;
    };

  const handleRemove = (fileIndex) => {
    const newFiles = { ...files };
    newFiles[fileIndex] = null;
    setFiles(newFiles);
    message.success('Archivo eliminado');
  };

  const handleJudgeChange = async (value) => {
    try{
      const judge = availableJudges.find(c => c.id === value);
      setJudgesIds([...judgeIds, { ...judge, id: value }]);
      form.setFieldValue('judges', null);
    } catch (error) {
      message.error('No se pudo agregar el juez');
    }
  };

  const removeJudge = (judgeId) => {
    setJudgesIds(judgeIds.filter(c => c.id !== judgeId));
  };

  const handleCriteriaChange = (value) => {
    try{
      const criterion = availableCriteria.find(c => c.id === value);
      if (criterion && totalScore + criterion.score <= 100) {
        setCriteriaIds([...criteriaIds, { ...criterion, id: value }]);
      } else {
        message.error('El total de puntos no puede superar 100');
      }
      form.setFieldValue('criteria', null);
    } catch (error) {
      message.error('No se pudo agregar el criterio de evaluación');
    }
  };

  const removeCriteria = (criteriaId) => {
    setCriteriaIds(criteriaIds.filter(c => c.id !== criteriaId));
  };

  const handleScoreChange = (id, newScore) => {
    setCriteriaIds(prev => {
      const currentItem = prev.find(item => item.id === id);
      const oldScore = Number(currentItem?.score) || 0;
      const value = Number(newScore) || 0;

      const currentTotal = prev.reduce(
        (sum, item) => sum + (Number(item.score) || 0),
        0
      );

      const nextTotal = currentTotal - oldScore + value;
      if (nextTotal > 100) {
        message.warning('El puntaje total no puede superar 100');
        return prev;
      }

      return prev.map(item =>
        item.id === id
          ? { ...item, score: value }
          : item
      );
    });
  };

  useEffect(() => {
    const total = criteriaIds.reduce(
      (sum, item) => sum + (Number(item.score) || 0),
      0
    );

    setTotalScore(total);
  }, [criteriaIds]);


  return (
    <div style={{ padding: 24 }}>
      {!competitionData && isEdit &&(<Alert
        type="warning"
        message="No se encuentra información de la competencia"
        showIcon
      />)}
      <Card title={isEdit ? 'Editar competencia' : 'Crear competencia'} loading={loading}>
        {!['En Configuración'].includes(competitionData?.status?.description) && id && (
          <Alert
            type="warning"
            message="La competencia ya no se puede editar, solo puedes editar cuando una competencia se encuentre 'En Configuración'"
            showIcon
          />
        )}
        <Form
          disabled={!['En Configuración'].includes(competitionData?.status?.description) && id}
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
          onValuesChange={handleFormChange}
        >
          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Modalidad"
                name="modalityId"
                rules={[
                  { required: true, message: 'La modalidad es requerida' },
                ]}
              >
                <Select
                  placeholder="Selecciona una modalidad"
                  options={modalities.map((modality) => ({
                    value: modality.id,
                    label: modality.description,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Periodo"
                name="periodId"
                rules={[
                  { required: true, message: 'El periodo es requerido' },
                ]}
              >
                <Select
                  placeholder="Selecciona un periodo"
                  options={periods.map((period) => ({
                    value: period.id,
                    label: period.description,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Genero"
                name="genderId"
                rules={[
                  { required: true, message: 'El genero es requerido' },
                ]}
              >
                <Select
                  placeholder="Selecciona un genero"
                  options={genders.map((gender) => ({
                    value: gender.id,
                    label: gender.description,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Estatus"
                name="statusId"
                rules={[
                  { required: true, message: 'El estatus es requerido' },
                ]}
              >
                <Select
                  placeholder="Selecciona un estatus"
                  options={statuses.map((status) => ({
                    value: status.id,
                    label: status.description,
                  }))}
                  disabled={true}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Card
            title="Fechas de la Competencia"
            style={{ borderRadius: 8 }}
          >
            {/* Inicio / Fin del concurso (informativo) */}
            <div
              style={{
                background: '#f0f0f0',
                borderRadius: 8,
                padding: 16,
                marginBottom: 24,
              }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Typography.Text type="secondary">
                    Inicio
                  </Typography.Text>
                  <div style={{ color: '#999' }}>
                    {startCompetition}
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <Typography.Text type="secondary">
                    Fin
                  </Typography.Text>
                  <div style={{ color: '#999' }}>
                    {endCompetition}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Etapas */}
            <div style={{ paddingLeft: 24 }}>
              {/* Convocatoria */}
              <Typography.Title level={5}>Convocatoria (subir videos)</Typography.Title>

              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'CALL', 'startDate']} label="Fecha inicio" rules={[{ required: true }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'CALL', 'startTime']} label="Hora inicio" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'CALL', 'endDate']} label="Fecha fin" rules={[{ required: true }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'CALL', 'endTime']} label="Hora fin" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              {/* Evaluación */}
              <Typography.Title level={5}>Evaluación</Typography.Title>

              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'EVALUATION', 'startDate']} label="Fecha inicio" rules={[{ required: true }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'EVALUATION', 'startTime']} label="Hora inicio" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'EVALUATION', 'endDate']} label="Fecha fin" rules={[{ required: true }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'EVALUATION', 'endTime']} label="Hora fin" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              {/* Votaciones */}
              <Typography.Title level={5}>Votaciones</Typography.Title>

              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'VOTING', 'startDate']} label="Fecha inicio" rules={[{ required: true }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'VOTING', 'startTime']} label="Hora inicio" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'VOTING', 'endDate']} label="Fecha fin" rules={[{ required: true }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'VOTING', 'endTime']} label="Hora fin" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              {/* Publicación de resultados */}
              <Typography.Title level={5}>Publicación de resultados</Typography.Title>

              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'RESULTS_PUBLICATION', 'startDate']} label="Fecha" rules={[{ required: true }]}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name={['stages', 'RESULTS_PUBLICATION', 'startTime']} label="Hora" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Card>


          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Folio"
                name="folio"
                rules={[
                ]}
              >
                <Input
                  placeholder="MG-SV-251221"
                  allowClear
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={18}>
              <Form.Item
                label="Descripción"
                name="description"
                rules={[
                  { max: 150, message: 'La descripción no puede exceder 150 caracteres' },
                  { required: true, message: 'La descripción es requerida' },
                ]}
              >
                <Input
                  placeholder="Titulo"
                  allowClear
                  maxLength={150}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Videos finalistas"
                name="finalistLimit"
                rules={[
                  { required: true, message: 'Los Videos finalistas son requeridos' },
                ]}
              >
                <InputNumber
                  placeholder="20"
                  min={1}
                  style={{ width: '30%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Enviar notificación"
                style={{ marginBottom: 0 }}
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item
                    noStyle
                    name="sendNotification"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    name="daysBefore"
                  >
                    <InputNumber
                      placeholder="Valor"
                      min={1}
                      style={{ width: '30%' }}
                    />
                  </Form.Item>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40%',
                    padding: '0 8px',
                    height: 32,
                  }}>
                    <Text type="secondary">días antes de que inicie la competencia.</Text>
                  </div>
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label="Imagen"
                required
                rules={[
                  {
                    validator: () => {
                      if (!imageFile && !originalPreviewPath && !isEdit) {
                        return Promise.reject(new Error('La imagen es requerida'));
                      }
                      if (imageRemoved && !imageFile) {
                        return Promise.reject(new Error('La imagen es requerida'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 220px', // Dragger ocupa 1fr, preview 220px
                    gap: 16,
                    width: '100%',
                  }}
                >
                  {/* Dragger */}
                  <Dragger {...uploadProps} style={{ minHeight: 150 }}>
                    <p className="ant-upload-drag-icon">
                      <PictureOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                    </p>
                    <p className="ant-upload-text">Subir imagen</p>
                    <p className="ant-upload-hint">
                      Haz clic o arrastra una imagen aquí para subirla (solo .jpg o .png)
                    </p>
                  </Dragger>

                  {/* Vista previa de la imagen */}
                  {imagePreview && (
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: 200,
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '1px solid #d9d9d9',
                        }}
                      />
                      <Button
                        type="link"
                        danger
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview(undefined);
                          setImageRemoved(true);
                        }}
                        style={{ display: 'block', marginTop: 8 }}
                      >
                        Eliminar imagen
                      </Button>
                    </div>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label="Instrucciones"
                name="instructions"
              >
                <RichTextEditor
                  editorState={instructionsState}
                  onEditorStateChange={onInstructionsChange}
                  placeholder="Indica pasos, recomendaciones o advertencias de ejecución aquí..."
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Upload
                style={{ marginBottom: 16 }}
                accept=".pdf"
                fileList={files.file1 ? [files.file1] : []}
                onRemove={() => handleRemove('file1')}
                onPreview={() => window.open(files.file1.url, '_blank')}
                beforeUpload={() => false} // Prevent automatic upload
                onChange={({ file }) => handleUpload(file, 'file1')}
              >
                <Button icon={<UploadOutlined />}>Adjuntar PDF bases del concurso</Button>
              </Upload>

              <Upload
                accept=".pdf"
                fileList={files.file2 ? [files.file2] : []}
                style={{ marginBottom: 16 }}
                beforeUpload={() => false} // Prevent automatic upload
                onRemove={() => handleRemove('file2')}
                onPreview={() => window.open(files.file2.url, '_blank')}
                onChange={({ file }) => handleUpload(file, 'file2')}
              >
                <Button icon={<UploadOutlined />}>Adjuntar PDF reglamento general</Button>
              </Upload>
              
              <Upload
                accept=".pdf"
                style={{ marginBottom: 16 }}
                fileList={files.file3 ? [files.file3] : []}
                beforeUpload={() => false} // Prevent automatic upload
                onRemove={() => handleRemove('file3')}
                onPreview={() => window.open(files.file3.url, '_blank')}
                onChange={({ file }) => handleUpload(file, 'file3')}
              >
                <Button icon={<UploadOutlined />}>Adjuntar PDF términos y condiciones</Button>
              </Upload>
            </Col>
            <Col xs={24} sm={12}>
              <Table
                columns={regionColumns}
                dataSource={regions}
                pagination={false}
                scroll={{ x: 'max-content' }}
                rowKey="id"
              />
            </Col>
          </Row>

          <Card
            title="Configuración de porcentaje de votos"
            style={{ borderRadius: 8 }}
          >
            

            {/* Etapas */}
            <div style={{ paddingLeft: 24 }}>
              
              <Row gutter={16}>
                {/* Publico */}
                <Col xs={24} md={12}>
                  <Typography.Title level={5}>Voto del publico</Typography.Title>
                  <Form.Item
                    label="Porcentaje"
                    name="publicVote"
                    dependencies={['juryVote']}
                    rules={[
                      { required: true, message: 'El voto del public es requerido' },
                      {
                        validator: (_, value) => {
                          const juryVote = form.getFieldValue('juryVote') || 0;
                          if ((value || 0) + juryVote !== 100) {
                            return Promise.reject(
                              new Error('La suma entre público y jurado debe ser 100')
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      placeholder="20"
                      min={0}
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                </Col>
                {/* Jyurado */}
                <Col xs={24} md={12}>
                  <Typography.Title level={5}>Voto del jurado</Typography.Title>
                  <Form.Item
                    label="Porcentaje"
                    name="juryVote"
                    dependencies={['publicVote']}
                    rules={[
                      { required: true, message: 'El voto del jurado es requerido' },
                      {
                        validator: (_, value) => {
                          const publicVote = form.getFieldValue('publicVote') || 0;
                          if ((value || 0) + publicVote !== 100) {
                            return Promise.reject(
                              new Error('La suma entre público y jurado debe ser 100')
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      placeholder="20"
                      min={0}
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Card>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                  label="Agregar Jueces"
                  name="judges"
                >
                <Select
                  showSearch
                  placeholder="Seleccionar juez"
                  onChange={handleJudgeChange}
                  options={availableJudges.map((judge) => ({
                    value: Number(judge.id),
                    label: `${judge.firstName} ${judge.lastName} ${judge.secondLastName || ''}`,
                  }))}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                </Select>
              </Form.Item>
                <Table
                  rowKey="id"
                  columns={judgeColumns}
                  dataSource={judgeIds}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: 'Sin jueces asignados' }}
                />
                <div>
                  {!availableJudges.length && !judgeIds.length && (
                    <Alert
                      type="warning"
                      message="No hay jurados compatibles"
                      showIcon
                    />
                  )}
                </div>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                  label="Agregar Criterios de Evaluación"
                  name="criteria"
              >
                <Select
                  showSearch
                  placeholder="Seleccionar Criterio"
                  onChange={handleCriteriaChange}
                  options={availableCriteria.map((criteria) => ({
                    value: Number(criteria.id),
                    label: criteria.description,
                  }))}
                >
                </Select>
              </Form.Item>
              <Table
                rowKey="id"
                columns={criteriaColumns}
                dataSource={criteriaIds}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
              <div>
                <h3>Total Puntos: {totalScore || 0}</h3>
                {totalScore !== 100 && (
                  <Alert
                    type="warning"
                    message="La competencia no será aprobada hasta que el total de los criterios sea 100"
                    showIcon
                  />
                )}
              </div>
            </Col>
          </Row>

          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate(-1)} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            {canAny(['competitions.create', 'competitions.update']) && (<Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? 'Guardar cambios' : 'Guardar'}
              </Button>
            </Col>)}
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default CompetitionUpsertPage;

